import {
	MouseEvent,
	PropsWithChildren,
	ReactElement,
	useMemo
} from "react";
import {
	Anchor,
	DateString,
	FontIconName,
	FormattedText,
	Icon,
	Space,
	PipeSeparator,
	IconAndText
} from "client/Components";
import {
	HubItemMediaKind,
	HubItemMediaPreviewViewModel,
	ReactionCounterModel
} from "client/Api/Hub/common";
import { HubItemCategoryModel } from "client/Api/Hub/hub";
import { ActiveFilter } from "client/Features/Hub/common";
import { HubItemKind } from "client/Api/Hub/hubItemCategories";
import { localize } from "client_legacy/ndaw/culture";
import { EventLocationType, PreviewItemUserModel } from "client/Api/Hub/Events/eventViewer";
import styles from "./HubCardItem.module.less";
import { TimeZone } from "client/DateAndTime/timezones";
import { isNotNil, setTrackingId } from "client/Misc/utils";
import { HubThumbnail } from "client/Features/Hub/HubThumbnail/HubThumbnail";
import { useHubStatisticsFormatter } from "client/Features/Hub/useHubStatisticsFormatter";
import { NumberFormatter } from "client/Hooks";
import { convertToDateOnly } from "client/DateAndTime/dateUtils";
import classNames from "classnames";
import { CardStatus } from "client/Features/Workspace/Tiles/Hub/Card/CardStatus/CardStatus";
import { ReactionStatistic, useReactionStatistic } from "client/Features/Reactions/useReactionStatistic";

type HubCardItemProps = {
	title: string;
	category: HubItemCategoryModel | undefined;
	author: PreviewItemUserModel | null;
	location: string | null;
	startDate: Date;
	endDate: Date;
	attendeeCount: number | null;
	media: HubItemMediaPreviewViewModel;
	onNavigate(): void;
	onActiveFilterChange(filter: ActiveFilter): void;
	onSelectedCategoryIdChange(selectedCategoryId: number | null): void;
	trackingId: string;
	timezone: TimeZone;
	locationType: EventLocationType;
	isNew: boolean;
	numberOfComments: number;
	reactionCounters: readonly ReactionCounterModel[];
	areCommentsEnabled: boolean;
};

export const HubCardItem = (props: HubCardItemProps): ReactElement => {
	const {
		title,
		category,
		author,
		location,
		startDate,
		endDate,
		attendeeCount,
		media,
		onNavigate,
		onActiveFilterChange,
		onSelectedCategoryIdChange,
		trackingId,
		timezone,
		locationType,
		isNew,
		numberOfComments,
		reactionCounters,
		areCommentsEnabled
	} = props;

	const statisticsFormatter: NumberFormatter = useHubStatisticsFormatter();
	const reactionStatistic: ReactionStatistic = useReactionStatistic(reactionCounters);

	const isSameDay: boolean = useMemo(
		(): boolean => {
			return convertToDateOnly(startDate, timezone)
				.isEqualTo(convertToDateOnly(endDate, timezone));
		},
		[
			endDate,
			startDate,
			timezone
		]
	);

	return (
		<div
			{...setTrackingId(trackingId)}
			className={styles.root}
			onClick={() => onNavigate()}
		>
			<div className={
				classNames(
					styles.thumbnail,
					{
						[styles.thumbnail_withBorder]: media.mediaKind === HubItemMediaKind.Image
					}
				)
			}>
				<div className={styles.thumbnailScaleContainer}>
					<HubThumbnail
						title={title}
						media={media}
						hubItemKind={HubItemKind.Event}
						mediaPlaceholderSize="large"
					/>
				</div>
				{isNew && (
					<div className={styles.new}>
						<CardStatus
							trackingId="status"
						>
							{localize("_New")}
						</CardStatus>
					</div>
				)}
			</div>
			<div className={styles.content}>
				<div className={styles.calendarIndicatorContainer}>
					<CalendarIndicator
						startDate={startDate}
						timezone={timezone}
					/>
				</div>
				<div className={styles.titleAndInfo}>
					<h4
						{...setTrackingId("title")}
						className={styles.title}
					>
						{title}
					</h4>

					<div {...setTrackingId("info")} className={styles.details}>
						{author && (
							<>
								{localize("_(Edited)By")}<Space />
								<Anchor
									className={styles.link}
									trackingId="author"
									onClick={
										(e: MouseEvent<HTMLAnchorElement>) => {
											e.stopPropagation();
											onActiveFilterChange({
												kind: "author",
												value: author
											});
										}
									}
								>
									{author.name}
								</Anchor>
							</>
						)}
						{category && (
							<>
								{author && (
									<PipeSeparator />
								)}
								<Anchor
									className={styles.link}
									trackingId="category"
									onClick={(e: MouseEvent<HTMLAnchorElement>) => {
										e.stopPropagation();
										onSelectedCategoryIdChange(category.id);
									}}
								>
									{category.name}
								</Anchor>
							</>
						)}
					</div>

					<ul className={styles.infoList}>
						<InfoItem
							trackingId="date-span"
							icon="clock"
						>
							{
								isSameDay ? (
									<>
										<DateString
											timezone={timezone}
											value={startDate}
											format="T"
										/>
										<Space />
										-
										<Space />
										<DateString
											timezone={timezone}
											value={endDate}
											format="T"
										/>
									</>
								) : (
									<FormattedText
										text={
											localize("_EventDateSpan")
										}
										thingsToReplace={[
											{
												what: "{{StartTime}}",
												filler: (
													<DateString
														timezone={timezone}
														value={startDate}
														format="T"
													/>
												)
											},
											{
												what: "{{EndDateTime}}",
												filler:(
													<DateString
														timezone={timezone}
														value={endDate}
														format="DMYT"
													/>
												)
											}
										]}
									/>
								)
							}
						</InfoItem>
						{isNotNil(location)
							&& (locationType === EventLocationType.Hybrid || locationType === EventLocationType.Physical)
							&& (
								<InfoItem
									trackingId="location"
									icon="location-dot"
								>
									{location}
								</InfoItem>
							)
						}
						{(locationType === EventLocationType.Hybrid || locationType === EventLocationType.Online) && (
							<InfoItem
								trackingId="online-meeting"
								icon="video"
							>
								{localize("_Online")}
							</InfoItem>
						)}

						<div className={styles.stats}>
							{isNotNil(attendeeCount) && (
								<IconAndText
									text={statisticsFormatter(attendeeCount)}
									trackingId="attendees"
								>
									<IconAndText.Icon
										value="user-group"
										size="small"
									/>
								</IconAndText>
							)}
							<IconAndText
								text={statisticsFormatter(reactionStatistic.total)}
								trackingId="reaction-count"
							>
								<IconAndText.Icon
									value={reactionStatistic.icon}
									size="small"
								/>
							</IconAndText>
							{areCommentsEnabled && (
								<IconAndText
									text={statisticsFormatter(numberOfComments)}
									trackingId="comment-count"
								>
									<IconAndText.Icon
										value="comment"
										size="small"
									/>
								</IconAndText>
							)}
						</div>
					</ul>
				</div>
			</div>
		</div>
	);
};
HubCardItem.displayName = "HubCardItem";

type CalendarIndicatorProps = {
	startDate: Date;
	timezone: TimeZone;
};

const CalendarIndicator = (props: CalendarIndicatorProps): ReactElement => {
	const {
		startDate,
		timezone
	} = props;

	return (
		<div className={styles.calendarIndicator}>
			<div className={styles.calendarIndicatorHeader}>
				<DateString
					format="MMM"
					value={startDate}
					timezone={timezone}
				/>
			</div>
			<div className={styles.calendarIndicatorContent}>
				<DateString
					format="D"
					value={startDate}
					timezone={timezone}
				/>
			</div>
		</div>
	);
};
CalendarIndicator.displayName = "CalendarIndicator";

type InfoItemProps = {
	icon: FontIconName;
	trackingId: string;
};

const InfoItem = (props: PropsWithChildren<InfoItemProps>): ReactElement => {
	const {
		icon,
		trackingId,
		children
	} = props;

	return (
		<li
			{...setTrackingId(trackingId)}
			className={styles.infoItem}
		>
			<Icon value={icon} />
			<div className={styles.infoItemText}>
				{children}
			</div>
		</li>
	);
};
InfoItem.displayName = "InfoItem";
